
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Navigation from '../../components/feature/Navigation';
import Card from '../../components/base/Card';

interface DashboardStats {
  totalJobs: number;
  activeJobs: number;
  completedJobs: number;
  pendingPayments: number;
}

export default function Home() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalJobs: 0,
    activeJobs: 0,
    completedJobs: 0,
    pendingPayments: 0
  });

  useEffect(() => {
    // Load dashboard stats from localStorage
    const jobs = JSON.parse(localStorage.getItem('apostille_jobs') || '[]');
    const activeJobs = jobs.filter((job: any) => job.status !== 'completed').length;
    const completedJobs = jobs.filter((job: any) => job.status === 'completed').length;
    const pendingPayments = jobs.filter((job: any) => !job.paymentReceived).length;

    setStats({
      totalJobs: jobs.length,
      activeJobs,
      completedJobs,
      pendingPayments
    });
  }, []);

  const quickActions = [
    {
      title: 'New Job',
      icon: 'ri-add-circle-line',
      color: 'bg-blue-500',
      action: () => navigate('/jobs/new')
    },
    {
      title: 'View Jobs',
      icon: 'ri-file-list-3-line',
      color: 'bg-green-500',
      action: () => navigate('/jobs')
    },
    {
      title: 'Clients',
      icon: 'ri-user-line',
      color: 'bg-purple-500',
      action: () => navigate('/clients')
    },
    {
      title: 'Countries',
      icon: 'ri-global-line',
      color: 'bg-orange-500',
      action: () => navigate('/countries')
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="Apostille Tracker" />
      
      <div className="pt-16 pb-20 px-4">
        {/* Welcome Section */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Welcome to Apostille Tracker</h2>
          <p className="text-gray-600 text-sm">Manage your apostille services efficiently</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.totalJobs}</div>
              <div className="text-sm text-gray-600">Total Jobs</div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{stats.activeJobs}</div>
              <div className="text-sm text-gray-600">Active Jobs</div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{stats.completedJobs}</div>
              <div className="text-sm text-gray-600">Completed</div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{stats.pendingPayments}</div>
              <div className="text-sm text-gray-600">Pending Payment</div>
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-4">
            {quickActions.map((action, index) => (
              <Card key={index} className="p-4" onClick={action.action}>
                <div className="flex flex-col items-center text-center">
                  <div className={`w-12 h-12 ${action.color} rounded-full flex items-center justify-center mb-3`}>
                    <i className={`${action.icon} text-white text-xl`}></i>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{action.title}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <Card className="p-4">
            <div className="text-center text-gray-500 py-8">
              <i className="ri-file-list-line text-4xl mb-2"></i>
              <p className="text-sm">No recent activity</p>
              <p className="text-xs text-gray-400 mt-1">Create your first job to get started</p>
            </div>
          </Card>
        </div>
      </div>

      <Navigation />
    </div>
  );
}
